import React from 'react';

import { DateTime } from 'luxon';
import { Row, Col, Form } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { useAppSelector } from 'hooks';

import DatePicker from './DatePicker';

// minToday: if the selected date is today, the min time is now
// maxToday: if the selected date is today, the max time is now
interface IDateTimePickerProps {
  dataTestPrefix?: string;
  date: DateTime;
  todayLimit?: 'min' | 'max';
  minDate?: DateTime;
  maxDate?: DateTime;
  onChangeDate: (selectedDate: DateTime) => void;
  dateLabelKey?: string;
  containerClassName?: string;
}

const DateTimePicker = ({
  dataTestPrefix,
  date,
  todayLimit,
  minDate,
  maxDate,
  onChangeDate,
  dateLabelKey,
  containerClassName
}: IDateTimePickerProps) => {
  const intl = useIntl();
  const locale = useAppSelector((state) => state.intl.locale);

  const today = DateTime.now();
  const now = DateTime.now().setLocale(locale).toLocaleString(DateTime.TIME_24_SIMPLE);

  const minMaxTime = today.hasSame(date, 'day') ? now : undefined;

  const handleDateChange = (newDate: Date) => {
    updateDate(DateTime.fromJSDate(newDate).setLocale(locale));
  };

  const handleTimeChange = (newTime: string) => {
    const [hours, minutes] = newTime.split(':');
    const newDate = date.setLocale(locale).set({ hour: Number(hours), minute: Number(minutes) });
    updateDate(newDate);
  };

  const updateDate = (newDate: DateTime) => {
    if (minDate && newDate < minDate) {
      onChangeDate(minDate);
    } else if (maxDate && newDate > maxDate) {
      onChangeDate(maxDate);
    } else {
      onChangeDate(newDate);
    }
  };

  return (
    <Row className={containerClassName}>
      <Col>
        <Form.Group className="form-group">
          <Form.Label>{intl.formatMessage({ id: dateLabelKey || 'dateKey' })}</Form.Label>
          <DatePicker
            dataTestId={`${dataTestPrefix}-date-picker`}
            name="selectedDate"
            className="w-100"
            wrapperClassName="w-100"
            onChange={(date) => date && handleDateChange(date)}
            selected={date.toJSDate()}
            minDate={minDate?.toJSDate()}
            maxDate={maxDate?.toJSDate()}
            popperClassName="z-100"
          />
        </Form.Group>
      </Col>
      <Col>
        <Form.Group className="form-group">
          <Form.Label>{intl.formatMessage({ id: 'timeKey' })}</Form.Label>
          <Form.Control
            data-test={`${dataTestPrefix}-time-picker`}
            type="time"
            name="time"
            value={date.setLocale(locale).toLocaleString(DateTime.TIME_24_SIMPLE)}
            onChange={(e: any) => handleTimeChange(e.target.value)}
            min={todayLimit === 'min' ? minMaxTime : undefined}
            max={todayLimit === 'max' ? minMaxTime : undefined}
          />
        </Form.Group>
      </Col>
    </Row>
  );
};

export default DateTimePicker;
